import atestadosData from 'data/atestados.json';

export const getTiposAtestado = () => atestadosData.atestados;

export const getAtestadoById = (id) => atestadosData.atestados.find((a) => a.id === id) || null;

export const gerarTextoAtestado = (tipoId, dados) => {
  const atestado = getAtestadoById(tipoId);
  if (!atestado) return '';

  let texto = atestado.template;

  // Substituir placeholders
  texto = texto.replace(/{NOME_PACIENTE}/g, dados.nomePaciente || '');
  texto = texto.replace(/{RG}/g, dados.rg || '');
  texto = texto.replace(/{DATA_EXTENSO}/g, dados.dataExtenso || '');
  texto = texto.replace(/{HORA_INICIO}/g, dados.horaInicio || '');
  texto = texto.replace(/{HORA_FIM}/g, dados.horaFim || '');
  texto = texto.replace(/{DIAS}/g, dados.dias || '');
  texto = texto.replace(/{CID}/g, dados.cid || '');

  return texto;
};

export const formatarDataExtenso = (date) => {
  const meses = [
    'janeiro',
    'fevereiro',
    'março',
    'abril',
    'maio',
    'junho',
    'julho',
    'agosto',
    'setembro',
    'outubro',
    'novembro',
    'dezembro',
  ];

  const dia = date.getDate();
  const mes = meses[date.getMonth()];
  const ano = date.getFullYear();

  return `${dia} de ${mes} de ${ano}`;
};

export const formatarDataNascimento = (paciente) => {
  if (paciente?.dataNasc) return paciente?.dataNasc; // data já formatada

  const dataOriginal = paciente?.DTNPAC;
  if (!dataOriginal) return '-';

  const data = new Date(dataOriginal);
  if (Number.isNaN(data.getTime())) {
    return dataOriginal;
  }

  return data.toLocaleDateString('pt-BR', { timeZone: 'UTC' });
};

export default async function fetchImageBase64(imagePath) {
  const res = await fetch(imagePath);
  if (!res.ok) {
    throw new Error(`Falha ao carregar imagem (${res.status})`);
  }
  const blob = await res.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      if (reader.error) {
        reject(reader.error);
        return;
      }
      resolve(reader.result);
    };
    reader.onerror = () => reject(reader.error || new Error('Falha ao ler imagem'));
    reader.onabort = () => reject(new Error('Leitura de imagem abortada'));
    reader.readAsDataURL(blob);
  });
}

export function formatarHora(hrAgenda) {
  let ao_hora = String(hrAgenda);
  if (ao_hora.length < 4) {
    ao_hora = `0${ao_hora}`;
  }
  ao_hora = ao_hora.replace(/(\d{2})(\d{2})$/, '$1:$2'); // Coloca um hífen entre o 3o e o 4o dígitos
  return ao_hora;
}

export function formatDateISO(date) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function mascaraCPF(val) {
  if (!val) return '';
  const apenasNumeros = String(val).replace(/\D/g, '').slice(0, 11);
  return apenasNumeros
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
}

/** CPF placeholder usado na clínica; não dispara checagem de duplicidade (alinhado ao backend). */
export function isCpfGenericoPaciente(cpf) {
  if (!cpf || String(cpf).trim() === '') return false;
  return String(cpf).replace(/\D/g, '') === '11111111111';
}

export function isValidCPF(cpf) {
  const cleanCpf = cpf.replace(/\D/g, '');
  if (cleanCpf.length !== 11 || /^(\d)\1{10}$/.test(cleanCpf)) return false;

  let sum = 0;
  for (let i = 1; i <= 9; i++) sum += parseInt(cleanCpf.substring(i - 1, i)) * (11 - i);
  let remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cleanCpf.substring(9, 10))) return false;

  sum = 0;
  for (let i = 1; i <= 10; i++) sum += parseInt(cleanCpf.substring(i - 1, i)) * (12 - i);
  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cleanCpf.substring(10, 11))) return false;

  return true;
}

// 6-doppler/18-cong/7-stress/14-stress/16-carotida/27-trans/38-strain/26-tilt/5-ergo
export const laudos = {
  doppler: 6,
  cong: 18,
  carotida: 16,
  trans: 27,
  strain: 38,
  tilt: 26,
  ergo: 5,
  consultaRetorno: [1, 11, 800],
  stress: [7, 14],
  ecg: [2, 40],
};

const rotasLaudos = {
  laudar: {
    [laudos.carotida]: '/formulario-carotida/:id',
    [laudos.ergo]: '/formulario-ergo/:id',
    [laudos.trans]: '/formulario-trans/:id',
    [laudos.doppler]: '/formulario-ecodoppler/:id',
    [laudos.strain]: '/formulario-ecodoppler/:id',
    [laudos.cong]: '/formulario-ecocongenito/:id',
    [laudos.stress[0]]: '/formulario-stress/:id',
    [laudos.stress[1]]: '/formulario-stress/:id',
  },
  visualizar: {
    [laudos.carotida]: '/visualizacao-carotida/:id',
    [laudos.ergo]: '/visualizacao-ergo/:id',
    [laudos.trans]: '/visualizacao-trans/:id',
    [laudos.tilt]: '/visualizacao-tilt/:id',
    [laudos.doppler]: '/visualizacao-ecodoppler/:id',
    [laudos.strain]: '/visualizacao-ecodoppler/:id',
    [laudos.cong]: '/visualizacao-ecocongenito/:id',
    [laudos.stress[0]]: '/visualizacao-stress/:id',
    [laudos.stress[1]]: '/visualizacao-stress/:id',
  },
};

export function isConsultaOuRetornoProcedi(codProcedi) {
  return laudos.consultaRetorno.includes(Number(codProcedi));
}

export function isConsultaOuRetornoAtividade(atividade) {
  const cod = Number(atividade?.CODPROCEDI);
  if (isConsultaOuRetornoProcedi(cod)) return true;

  const descricao = String(atividade?.DESCPROCEDI || '').toUpperCase();
  return descricao.includes('CONSULTA') || descricao.includes('RETORNO');
}

export function getAtividadeRoute({ action, codProcedi, codLaudo }) {
  const normalizedAction = String(action || '').toLowerCase();
  const cod = Number(codProcedi);
  const rawRoute = rotasLaudos?.[normalizedAction]?.[cod];

  if (!rawRoute) return null;
  return rawRoute.replace(':id', String(codLaudo));
}

export function abreviarNomePaciente(nomeCompleto) {
  const ignorar = ['de', 'da', 'do', 'das', 'dos', 'e'];

  const partes = String(nomeCompleto || '')
    .trim()
    .split(/\s+/)
    .filter((part) => !ignorar.includes(part.toLowerCase()));

  if (partes.length === 0) return '';
  if (partes.length === 1) return partes[0];

  const primeiroNome = partes[0];
  const ultimoNome = partes[partes.length - 1];

  const meioIniciais = partes
    .slice(1, -1)
    .map((nome) => `${nome.charAt(0).toUpperCase()}.`)
    .join(' ');

  const meioFormatado = meioIniciais ? ` ${meioIniciais}` : '';

  return `${primeiroNome}${meioFormatado} ${ultimoNome}`;
}

export function obterNomeTratado(nomeCompleto = 'Paciente', sexo = '', dataNascimento) {
  const nomes = nomeCompleto.split(' ').filter(Boolean);
  const artigos = ['DE', 'DA', 'DAS', 'DO', 'DOS', 'E'];
  let doisPrimeirosNomes = nomes[0] || '';

  if (nomes.length > 1) {
    if (artigos.includes(nomes[1].toUpperCase())) {
      doisPrimeirosNomes += ` ${nomes[1]}`;
      if (nomes.length > 2) doisPrimeirosNomes += ` ${nomes[2]}`;
    } else {
      doisPrimeirosNomes += ` ${nomes[1]}`;
    }
  }

  if (!dataNascimento) return doisPrimeirosNomes;

  const nascimento = new Date(dataNascimento);
  const hoje = new Date();
  let idade = hoje.getFullYear() - nascimento.getFullYear();
  const m = hoje.getMonth() - nascimento.getMonth();
  if (m < 0 || (m === 0 && hoje.getDate() < nascimento.getDate())) {
    idade--;
  }

  if (idade >= 18) {
    const tratamento = sexo === 'F' ? 'Sra.' : sexo === 'M' ? 'Sr.' : '';
    return tratamento ? `${tratamento} ${doisPrimeirosNomes}` : doisPrimeirosNomes;
  }

  return doisPrimeirosNomes;
}

export function mascaraTelefoneComZero(val) {
  if (!val) return '';

  const apenasNumeros = val.replace(/\D/g, '');
  const limpo = apenasNumeros.slice(0, 12);

  if (limpo.length > 11) {
    return limpo.replace(/^(\d{3})(\d{5})(\d{4})$/, '($1)$2-$3');
  } else if (limpo.length > 7) {
    return limpo.replace(/^(\d{3})(\d{4})(\d{4})$/, '($1)$2-$3');
  } else if (limpo.length > 3) {
    return limpo.replace(/^(\d{3})(.*)$/, '($1)$2');
  } else if (limpo.length > 0) {
    return `(${limpo}`;
  }

  return limpo;
}
